import { FilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import Text from '@shared/components/content/text';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { ColumnDef } from '@tanstack/react-table';
import { ReportType } from '../types';
import { ReportFilterColumn, reportFilterKeys } from './reports-filters';

type ReportFilterOptions = FilterOptions<typeof reportFilterKeys>;

export function useReportTableColumns({
  filters,
  reportType,
}: {
  filters: ReportFilterOptions;
  reportType?: ReportType;
}): ColumnDef<any>[] {
  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'title',
      accessorKey: 'title',
      header: ({ column }) => <HeaderCell column={column} name="Title" />,
      cell: ({ getValue }) => {
        return <Text>{getValue<string>()}</Text>;
      },
      enableColumnFilter: true,
      meta: {
        label: 'Search',
        variant: 'text',
        sorting: { ascLabel: 'A–Z', descLabel: 'Z–A' },
      },
      enableSorting: true,
    },
    {
      id: ReportFilterColumn.Tags,
      accessorKey: ReportFilterColumn.Tags,
      enableColumnFilter: true,
      meta: {
        label: 'Tag',
        variant: 'multiSelect',
        options: filters?.tags,
        sorting: { ascLabel: 'A–Z', descLabel: 'Z–A' },
      },
    },
    {
      id: ReportFilterColumn.Sites,
      accessorKey: ReportFilterColumn.Sites,
      enableColumnFilter: true,
      meta: {
        label: 'Site',
        variant: 'multiSelect',
        options: filters?.sites,
        sorting: { ascLabel: 'A–Z', descLabel: 'Z–A' },
      },
    },
    {
      id: ReportFilterColumn.TimePeriods,
      accessorKey: ReportFilterColumn.TimePeriods,
      enableColumnFilter: true,
      meta: {
        label: 'Period',
        variant: 'multiSelect',
        options: filters?.timePeriods,
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
    },
    // Customers only apply to sales reports.
    ...(reportType === 'sales'
      ? [
          {
            id: ReportFilterColumn.Customers,
            accessorKey: ReportFilterColumn.Customers,
            enableColumnFilter: true,
            meta: {
              label: 'Customer',
              variant: 'multiSelect',
              options: filters?.customers,
              sorting: { ascLabel: 'A–Z', descLabel: 'Z–A' },
            },
          } as ColumnDef<any>,
        ]
      : []),
    {
      id: ReportFilterColumn.GoodsType,
      accessorKey: ReportFilterColumn.GoodsType,
      enableColumnFilter: true,
      meta: {
        label: 'Type',
        variant: 'multiSelect',
        options: [
          { label: 'Food', value: 'food' },
          { label: 'Packaging', value: 'packaging' },
        ],
        sorting: { ascLabel: 'A–Z', descLabel: 'Z–A' },
      },
    },
    {
      id: 'created',
      accessorKey: 'created',
      enableSorting: true,
      meta: {
        label: 'Date',
        sorting: { ascLabel: 'Oldest first', descLabel: 'Newest first' },
      },
    },
    {
      id: 'emissions',
      accessorKey: 'totalEmissionsTonnes',
      enableSorting: true,
      meta: {
        label: 'Emissions',
        sorting: { ascLabel: 'Low to High', descLabel: 'High to Low' },
      },
    },
  ];
}
